import { useCallback, useMemo, useRef, useState, ChangeEvent } from "react";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { FixaToast } from "@coworker/apprestructured/src/shared/wrappers/FixaToast/FixaToast";
import { useTranslation } from "@coworker/locales";
import { isImageValid } from "./helpers";
import SortableItem from "./SortableItem/SortableItem";
import styles from "./AddImagesModal.module.css";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import ImageIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ImageIconPath";
import CameraIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CameraIconPath";
import {
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
  DragEndEvent,
  closestCenter,
} from "@dnd-kit/core";
import { verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { isDesktop } from "@coworker/apprestructured/src/scan-and-search/pages/default-scanner-page/defaultScannerPageUtils";
import {
  createNewOrExistingFileKey,
  fileToNewFile,
  NewFile,
  NewOrExistingFile,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";

const MAX_IMAGES = 50;

type AddImagesModalProps = {
  isOpen: boolean;
  images: NewOrExistingFile[];
  onImagesChange: (images: NewOrExistingFile[]) => void;
  onClose: () => void;
  onConfirmClick: () => void;
};

export const AddImagesModal = ({
  isOpen,
  images,
  onImagesChange,
  onClose,
  onConfirmClick,
}: AddImagesModalProps) => {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastText, setToastText] = useState("");

  const { t } = useTranslation();

  const imageFolderInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const isDesktopDevice = useMemo(() => isDesktop(), []);

  const handleImageFolderClick = useCallback(() => {
    imageFolderInputRef.current?.click();
  }, []);

  const handleCameraClick = useCallback(() => {
    cameraInputRef.current?.click();
  }, []);

  const prevImagesMap = useMemo(
    () => new Map(images.map((f) => [createNewOrExistingFileKey(f), f])),
    [images]
  );

  const closeAndResetModal = useCallback(() => {
    setToastText("");
    setToastOpen(false);
    onClose();
  }, [onClose]);

  const {
    saveGuardedFunction: handleCloseClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(closeAndResetModal);

  const handleImagesChange = useCallback(
    (newImages: NewOrExistingFile[]) => {
      onImagesChange(newImages);
      enableSaveGuard();
    },
    [onImagesChange, enableSaveGuard]
  );

  const handleAddImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const newImages = e.target.files ? Array.from(e.target.files) : [];
    const uniqueNewImages = newImages.filter(
      (f) => !prevImagesMap.has(createNewOrExistingFileKey(fileToNewFile(f)))
    );

    const validImages: NewFile[] = [];
    for (const file of uniqueNewImages) {
      const valid = await isImageValid(file);
      if (!valid) {
        setToastText(t("couldNotAddImageString", { imageName: file.name }));
        setToastOpen(true);
        continue;
      }
      validImages.push(fileToNewFile(file));
    }

    handleImagesChange([...images, ...validImages].slice(0, MAX_IMAGES));
    e.target.value = "";
  };

  const handleRemoveImage = useCallback(
    (imageToRemove: NewOrExistingFile) => {
      handleImagesChange(
        images.filter(
          (image) =>
            createNewOrExistingFileKey(image) !==
            createNewOrExistingFileKey(imageToRemove)
        )
      );
    },
    [images, handleImagesChange]
  );

  const handleConfirmClick = useCallback(() => {
    onConfirmClick();
    closeAndResetModal();
    disableSaveGuard();
  }, [onConfirmClick, closeAndResetModal, disableSaveGuard]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor)
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = images.findIndex(
        (f) => createNewOrExistingFileKey(f) === active.id
      );
      const newIndex = images.findIndex(
        (f) => createNewOrExistingFileKey(f) === over?.id
      );
      handleImagesChange(arrayMove(images, oldIndex, newIndex));
    }
  }

  return (
    <>
      <FixaModal visible={isOpen} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          labelledById="task-management-add-images-header"
          fullSize
          header={
            <FixaModalHeader
              id="task-management-add-images-header"
              closeBtnClick={handleCloseClick}
              title={t("addImagesString")}
            />
          }
          footer={
            <StyledModalFooter>
              <div className={styles["buttonGroup"]}>
                <div className={styles["buttonWrapper"]}>
                  <FixaButton
                    text={t("imageFolderString")}
                    ssrIcon={ImageIconPath}
                    size="xsmall"
                    onClick={handleImageFolderClick}
                    type="secondary"
                  />
                </div>
                <div className={styles["buttonWrapper"]}>
                  <FixaButton
                    text={t("cameraString")}
                    ssrIcon={CameraIconPath}
                    size="xsmall"
                    onClick={handleCameraClick}
                    disabled={isDesktopDevice}
                    type="secondary"
                  />
                </div>
              </div>
              <FixaButton
                text={t("confirmString")}
                type="primary"
                onClick={handleConfirmClick}
              />
            </StyledModalFooter>
          }
        >
          <FixaModalBody>
            <input
              type="file"
              accept="image/*"
              multiple
              className={styles["hiddenInput"]}
              ref={imageFolderInputRef}
              onChange={handleAddImage}
            />
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className={styles["hiddenInput"]}
              ref={cameraInputRef}
              onChange={handleAddImage}
            />
            {images.length === 0 && (
              <FixaText textStyle="Body.L">
                {t("noImagesSelectedString")}
              </FixaText>
            )}
            {images.length > 0 && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              >
                <SortableContext
                  items={images.map((f) => createNewOrExistingFileKey(f))}
                  strategy={verticalListSortingStrategy}
                >
                  <div className={styles["imagesWrapper"]}>
                    {images.map((image) => (
                      <SortableItem
                        key={createNewOrExistingFileKey(image)}
                        image={image}
                        images={images}
                        onRemove={handleRemoveImage}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
      <FixaToast
        isOpen={toastOpen}
        onCloseRequest={() => setToastOpen(false)}
        text={toastText}
      />
    </>
  );
};
